import { FunctionComponent, memo, useCallback, useContext, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { useNavigate } from 'react-router';
import { ChannelContext, UserContext } from '../../../context';
import { EventUserStatus, useEventsQuery } from '../../../services';
import {
  cookieOptions,
  EVENTS_FEED_ADMIN_COOKIE_KEY,
  EVENTS_FEED_COOKIE_KEY,
  getCssVar,
  IconLabel,
  isAdminLayout,
  layoutPath,
} from '../../../shared';
import { UpcomingEventCard } from './UpcomingEventCard';

import classes from './UpcomingEvents.module.scss';

export interface ConfigUpcomingEventsWidget {
  titleLocaliseKey: string;
  eventsLimit: number;
}

interface UpcomingEventsProps {
  config: ConfigUpcomingEventsWidget;
}

export const UpcomingEvents: FunctionComponent<UpcomingEventsProps> = memo(({ config }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const cookieKey = isAdminLayout() ? EVENTS_FEED_ADMIN_COOKIE_KEY : EVENTS_FEED_COOKIE_KEY;

  const [cookies, setCookie] = useCookies([cookieKey]);

  const { isAnonymous } = useContext(UserContext).userProfile;

  const { id: channelId } = useContext(ChannelContext).channel;

  const { titleLocaliseKey = 'Upcoming events', eventsLimit = 3 } = config;

  const handleViewAll = useCallback(() => {
    setCookie(
      cookieKey,
      {
        ...cookies[cookieKey],
        requestFilters: { participantStatuses: [EventUserStatus.CONFIRMED] },
        filter: [
          {
            groupId: 'participantStatuses',
            groupItems: [{ id: EventUserStatus.CONFIRMED, value: EventUserStatus.CONFIRMED }],
          },
        ],
      },
      cookieOptions()
    );
    navigate(layoutPath('/events'));
  }, [cookieKey, cookies, setCookie, navigate]);

  const from = useMemo(() => new Date().toISOString(), []);

  const { data, isFetching } = useEventsQuery(
    {
      channelId,
      size: eventsLimit,
      filters: { from, participantStatuses: [EventUserStatus.CONFIRMED] },
    },
    { skip: isAnonymous }
  );

  const events = useMemo(() => data?.items ?? [], [data?.items]);

  const renderEvents = useMemo(() => {
    if (!events.length) {
      return (
        <div className={classes['upcoming-events__empty']}>
          <IconLabel
            className={classes['upcoming-events__empty-icon']}
            iconId={'event'}
            iconSize={32}
            color={'#98A1A6'}
            singleColor
            nonClickable
          />
          {t('upcomingEvents.no-events')}
        </div>
      );
    }

    return events.map((event) => <UpcomingEventCard key={event.id} event={event} />);
  }, [events, t]);

  if (isAnonymous) {
    return null;
  }

  if (isFetching) {
    return (
      <div className={classes['upcoming-events__loader-wrapper']}>
        <Skeleton className={classes['upcoming-events__loader']} count={eventsLimit} />
      </div>
    );
  }

  return (
    <div className={classes['upcoming-events']}>
      <div className={classes['upcoming-events__header']}>
        <h2 className={classes['upcoming-events__header-title']}>{t(titleLocaliseKey)}</h2>
        {Boolean(events.length) && (
          <IconLabel
            iconSize={18}
            iconId={'right-arrow'}
            label={t('common.view-all')}
            color={getCssVar('--base-link-text-color')}
            hoverColor={getCssVar('--base-link-text-hover-color')}
            onClick={handleViewAll}
            labelFirst
          />
        )}
      </div>
      {renderEvents}
    </div>
  );
});
